import React from "react";
import { IntlShape, useIntl } from "react-intl";
import styled from "styled-components";
import { asUTC, asUTCTime } from "../../../lib/dateUtils";
import { ConfigurationElement, Renderer, RendererProps } from "./index";

const StyledButton = styled.span`
  margin-left: auto;
`;

const StyledStringRenderer = styled.div`
  margin-right: 20px;
  display: flex;
  align-items: center;
`;

const StyledStringCenterRenderer = styled.div`
  margin-right: 20px;
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
`;

export function BooleanRenderer({ value: valueReq = "" }: RendererProps<string | boolean>) {
  return <>{valueReq ? "✔" : "✘"}</>;
}

export function StringRenderer({
  value: valueReq = "",
  config,
  button,
  rows,
}: RendererProps<string | undefined>) {
  let value = valueReq;
  if (config?.isHtml) {
    const element = document.createElement("div");
    element.innerHTML = valueReq.replace(/<\/[^>]+(>|$)/g, " ").replace(/<\/?[^>]+(>|$)/g, "");
    value = element.innerText;
  }

  if (config?.maxCharacters && value.length > config.maxCharacters) {
    value = `${value.substring(0, config.maxCharacters)} [...]`;
  }

  if (config?.align === "center") {
    return (
      <StyledStringCenterRenderer>
        {value}
        {button && <StyledButton>{button(rows)}</StyledButton>}{" "}
      </StyledStringCenterRenderer>
    );
  }

  return (
    <StyledStringRenderer>
      {value}
      {button && <StyledButton>{button(rows)}</StyledButton>}{" "}
    </StyledStringRenderer>
  );
}

export function ArrayRenderer({ value }: RendererProps<string[] | readonly string[]>) {
  return <>{value?.join(", ")}</>;
}

export function NumberRenderer({ value }: RendererProps<number | undefined>) {
  if (!value) {
    return <span>-</span>;
  }
  return <span>{value.toFixed(2)}</span>;
}

export function IntegerRenderer({ value }: RendererProps<number | undefined>) {
  if (!value) {
    return <span>-</span>;
  }
  return <span>{value}</span>;
}

function calculateProtoDate({
  value,
}: // @ts-ignore
RendererProps<proto.google.protobuf.Timestamp.toObject>): Date | null {
  if (value?.seconds || value?.nanos) {
    // @ts-ignore
    const protoDate = new proto.google.protobuf.Timestamp();
    protoDate.setSeconds(value?.seconds);
    protoDate.setNanos(value?.nanos);
    return protoDate.toDate();
  }
  return null;
}

export function calculateDateFromProtoDate({
  value,
}: // @ts-ignore
RendererProps<proto.google.protobuf.Timestamp.toObject>) {
  const protoDate = calculateProtoDate({ value });
  if (protoDate == null) return null;
  return asUTC(protoDate);
}

export function calculateDateTimeFromProtoDate({
  value,
}: // @ts-ignore
RendererProps<proto.google.protobuf.Timestamp.toObject>) {
  const protoDate = calculateProtoDate({ value });
  if (protoDate == null) return null;
  return asUTCTime(protoDate, "yyyy-MM-dd HH:mm:ss");
}

// @ts-ignore
export function DateRenderer({ value }: RendererProps<proto.google.protobuf.Timestamp.toObject>) {
  const formattedDate = calculateDateFromProtoDate({ value });
  if (formattedDate == null) return null;
  return <>{formattedDate}</>;
}

export function DateTimeRenderer({
  value,
}: // @ts-ignore
RendererProps<proto.google.protobuf.Timestamp.toObject>) {
  const formattedDate = calculateDateTimeFromProtoDate({ value });
  if (formattedDate == null) return null;
  return <>{formattedDate}</>;
}

export function mapRenderer<I, O>(
  InnerRenderer: Renderer<O>,
  mapper: (t: I, intl: IntlShape) => O | undefined,
  config?: ConfigurationElement
): Renderer<I> {
  return function MappedRenderer({ value }: RendererProps<I>) {
    const intl = useIntl();
    return (
      <InnerRenderer
        config={config}
        value={typeof value !== "undefined" ? mapper(value, intl) : undefined}
      />
    );
  };
}

export function ReadOnlySwitchRenderer({ value }: RendererProps<boolean>) {
  return (
    <div className="SwitchGroup witadmin-bs3">
      <label className="switch">
        <input role="button" type="checkbox" readOnly={true} checked={value} />
        <em />
      </label>
    </div>
  );
}
